/**
 * The multi-family income fields of the analyze request: after-repair rent per unit
 * and GRM, which together drive the income-led ARV.
 *
 * THE BUG THIS EXISTS TO KILL (BACKLOG variant P4). The MF panel on /confirm renders on
 * the strength of the property TYPE alone — there is no strategy gate. But the payload
 * put `user_rent` behind `strategy !== 'rental'`, on the reasoning that a rental already
 * sends its own rent from the Analyzer. The result was a rental multi-family showing a
 * bold "Multi-Family Income (drives the ARV)" heading over a pre-filled, editable rent
 * that reached nothing. Confirmed on production: the panel is visible on a rental
 * confirm page and its copy promises the field produces "a real After-Repair Value".
 *
 * GRM never had the problem — it sat outside that guard and shipped for every strategy.
 * That asymmetry, one key inside the guard and one outside, is the whole defect.
 */

/** Units implied by a confirmed property type. 1 means "not multi-family". */
export const unitsFromType = (t) => {
  const s = (t || '').toLowerCase();
  if (s.includes('duplex')) return 2;
  if (s.includes('triplex')) return 3;
  if (s.includes('fourplex') || s.includes('quad')) return 4;
  if (s.includes('multi')) return 4;
  return 1;
};

export const isMultiFamilyType = (t) => unitsFromType(t) > 1;

/**
 * Fields to merge into the analyze request. Returns {} for a non-multi-family subject.
 *
 * @param {string} strategy           'flip' | 'rental' | 'valuation'
 * @param {string} propertyType       the CONFIRMED type
 * @param {*}      mfRentPerUnit      what the user typed in the panel (may be blank)
 * @param {*}      mfGrm              GRM from the panel (blank = value by sales)
 * @param {*}      analyzerUnitCount  the count the rental branch sends alongside its own rent
 */
export const buildMfIncomeFields = ({
  strategy,
  propertyType,
  mfRentPerUnit,
  mfGrm,
  analyzerUnitCount,
} = {}) => {
  if (!isMultiFamilyType(propertyType)) return {};

  const fields = { multi_family_grm: parseFloat(mfGrm) || null };
  const rentPerUnit = parseFloat(mfRentPerUnit) || 0;
  const isRental = strategy === 'rental';

  // Blank is a legitimate choice — the panel says so: "leave them blank and you'll only
  // see the conservative as-is value". On rental the override therefore applies ONLY
  // when a real figure was entered; otherwise the Analyzer's rent stands. Writing an
  // unconditional 0 here would wipe a rent the user had already given.
  if (!isRental || rentPerUnit > 0) {
    // The multiplier differs per strategy ON PURPOSE. A rental sends
    // `unit_count: analyzerUnitCount`, and the backend divides user_rent by that count
    // (server.py:1140). Multiplying by anything else would hand it a rent and a count
    // that disagree — the unit_count trap recorded in BACKLOG.md. This change must not
    // widen it, so rental keeps its own count and only the rent figure comes from the
    // panel.
    const multiplier = isRental
      ? (Number(analyzerUnitCount) || 1)
      : unitsFromType(propertyType);
    fields.user_rent = rentPerUnit * multiplier;
  }

  // unit_count is derived from the confirmed type for non-rental only. Rental sends its
  // own, above, and overwriting it here is exactly what must not happen.
  if (!isRental) {
    fields.unit_count = unitsFromType(propertyType);
  }

  return fields;
};

export default buildMfIncomeFields;
